import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useTenant } from '../../contexts/TenantContext';
import TcnAdminLayout from '../../components/tcn-admin/TcnAdminLayout';
import ExcelDropZone from '../../components/tcn-admin/ExcelDropZone';
import DryRunReport from '../../components/tcn-admin/DryRunReport';

/**
 * /tcn/admin/upload — primary ingestion screen for the TCN agency_admin.
 *
 * Flow:
 *   1. User drops a .xlsx file
 *   2. Frontend POSTs to /functions/v1/data-import-tcn/dry-run
 *   3. We render the DryRunReport
 *   4. User clicks "Send invites & Apply" → POSTs to /apply with validation_token
 *   5. On success → navigate to /tcn/admin/imports
 */
const TcnAdminUpload = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { agencyId } = useTenant();
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [missingCnid, setMissingCnid] = useState(false);

  // Onboarding nudge — flag if any of the agency's servers lacks a Creator
  // Network ID. Banner sends the owner to /tcn/admin/settings to fill it in.
  useEffect(() => {
    let cancelled = false;
    if (!agencyId) return () => { cancelled = true; };
    (async () => {
      const { data } = await supabase
        .from('agencies')
        .select('servers, creator_network_ids')
        .eq('id', agencyId)
        .maybeSingle();
      if (cancelled) return;
      const servers = data?.servers ?? [];
      const ids = data?.creator_network_ids ?? {};
      const missing = servers.some((s) => !(ids[s] && String(ids[s]).trim()));
      setMissingCnid(missing);
    })();
    return () => { cancelled = true; };
  }, [agencyId]);

  const fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/data-import-tcn`;

  const callEdge = async (endpoint, body, isJson = false) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');
    const headers = { Authorization: `Bearer ${session.access_token}` };
    if (isJson) headers['Content-Type'] = 'application/json';
    const resp = await fetch(`${fnUrl}/${endpoint}`, {
      method: 'POST',
      headers,
      body: isJson ? JSON.stringify(body) : body,
    });
    const json = await resp.json();
    if (!resp.ok) throw new Error(json.error || 'Edge Function failed');
    return json;
  };

  const handleFile = async (f) => {
    setError(null);
    setApplyResult(null);
    setFile(f);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const report = await callEdge('dry-run', fd);
      setDryRun(report);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!file || !dryRun?.validation_token) return;
    setApplying(true);
    setError(null);
    try {
      // Phase 1 — raw upserts (status='raw_applied')
      const fd = new FormData();
      fd.append('file', file);
      fd.append('validation_token', dryRun.validation_token);
      const result = await callEdge('apply', fd);
      setApplyResult(result);
      setApplying(false);

      // Phase 2 — recompute (status='applied')
      // Split because Edge Function timeout (~60s) can't fit both for >300 rows.
      if (result?.recompute_required && result?.data_import_id) {
        setRecomputing(true);
        try {
          await callEdge('recompute', { data_import_id: result.data_import_id }, true);
        } catch (e) {
          setError(`Apply OK but recompute failed: ${e.message}. Retry from history.`);
        } finally {
          setRecomputing(false);
        }
      }

      // After both phases, jump to history
      setTimeout(() => navigate('/tcn/admin/imports'), 1500);
    } catch (e) {
      setError(e.message);
      setApplying(false);
    }
  };

  const reset = () => {
    setFile(null);
    setDryRun(null);
    setError(null);
    setApplyResult(null);
  };

  return (
    <TcnAdminLayout
      title={t('tcn.upload.title', 'Upload Excel')}
      subtitle={t('tcn.upload.subtitle', 'Drop a TCN creator data file. Auto-detects EN, FR, AR.')}
    >
      {missingCnid && (
        <div className="rounded-xl border border-warning/30 bg-warning/5 px-4 py-3 mb-4 flex items-start gap-3 text-sm">
          <span className="text-warning text-base leading-tight">⚠</span>
          <div className="flex-1 text-foreground">
            <div className="font-medium">{t('tcn.admin.cnid.bannerTitle', 'Creator Network ID missing')}</div>
            <p className="text-muted-foreground mt-1">
              {t('tcn.admin.cnid.bannerBody', 'Add your TikTok Backstage Creator Network ID for each server before Phase 2 integration goes live.')}
            </p>
          </div>
          <Link
            to="/tcn/admin/settings"
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-primary-foreground hover:opacity-90 whitespace-nowrap"
          >
            {t('tcn.admin.cnid.bannerCta', 'Configure')}
          </Link>
        </div>
      )}
      {!dryRun && !applyResult && (
        <div className="space-y-4">
          <ExcelDropZone onFile={handleFile} disabled={loading} />
          {loading && (
            <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground flex items-center gap-3">
              <div className="w-4 h-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
              {t('tcn.upload.parsing', 'Parsing file…')}
            </div>
          )}
          {error && (
            <div className="rounded-xl border border-error/30 bg-error/5 p-4 text-sm text-error">
              {error}
            </div>
          )}
        </div>
      )}

      {dryRun && !applyResult && (
        <DryRunReport
          report={dryRun}
          onCancel={reset}
          onApply={handleApply}
          applying={applying}
        />
      )}

      {applyResult && (
        <div className={`rounded-xl border p-6 ${recomputing ? 'border-primary/30 bg-primary/5' : 'border-success/30 bg-success/5'}`}>
          <div className={`font-heading text-lg font-bold mb-2 flex items-center gap-3 ${recomputing ? 'text-primary' : 'text-success'}`}>
            {recomputing && <div className="w-4 h-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />}
            {recomputing
              ? t('tcn.upload.recomputing', 'Recomputing indicators…')
              : t('tcn.upload.success', 'Import applied')}
          </div>
          <div className="text-sm text-foreground/80 space-y-1">
            <div>data_import_id: <span className="font-mono">{applyResult.data_import_id}</span></div>
            <pre className="text-xs bg-card border border-border rounded-lg p-3 mt-2 overflow-auto">
              {JSON.stringify(applyResult.counts, null, 2)}
            </pre>
            <div className="text-xs text-muted-foreground mt-2">
              {recomputing
                ? t('tcn.upload.recomputingNote', 'Phase 2 of 2 — refreshing creator/manager current_month tables.')
                : t('tcn.upload.redirecting', 'Redirecting to history…')}
            </div>
          </div>
        </div>
      )}

      {error && dryRun && (
        <div className="rounded-xl border border-error/30 bg-error/5 p-4 text-sm text-error mt-4">
          {error}
        </div>
      )}
    </TcnAdminLayout>
  );
};

export default TcnAdminUpload;
